import { DatePicker } from '@components/common';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { selectEffectiveOutletId } from '@store/outlets/outletSelectors';
import { Download, RefreshCw } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { exportToCsv } from '../../accommodation/reports/reportUtils';
import {
  CogsReportRow,
  DeferredRevenueRow,
  PnlRow,
  TaxReportRow,
  fetchCogsReport,
  fetchDeferredRevenue,
  fetchProfitAndLoss,
  fetchTaxReport,
} from '../../../../services/accountingReportingService';
import { apiError } from '../../../../util/notify';
import { formatCurrencyValue } from './accountingReports';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtDate = (d: string) =>
  d
    ? new Date(d).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

const srcBadge = (src: string) => {
  const map: Record<string, string> = {
    sales: 'bg-green-50 text-green-700',
    inventory: 'bg-blue-50 text-blue-700',
    accommodation: 'bg-purple-50 text-purple-700',
    booking_com: 'bg-indigo-50 text-indigo-700',
    manual: 'bg-gray-50 text-gray-700',
    system: 'bg-yellow-50 text-yellow-700',
  };
  return map[src] ?? 'bg-gray-50 text-gray-600';
};

const PNL_SECTION_ORDER: PnlRow['section'][] = [
  'Revenue',
  'COGS',
  'Gross Profit',
  'Expenses',
  'Net Income',
];

// ─── Date Filter Controls ────────────────────────────────────────────────────

interface DateRangeProps {
  dateFrom: string;
  dateTo: string;
  isLoading: boolean;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  onRefresh: () => void;
  onExport?: () => void;
  canExport?: boolean;
}

const DateRangeControls: React.FC<DateRangeProps> = ({
  dateFrom,
  dateTo,
  isLoading,
  onFromChange,
  onToChange,
  onRefresh,
  onExport,
  canExport = false,
}) => (
  <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-white p-4">
    <DatePicker
      mode="single"
      id="fr-date-from"
      label="From"
      value={dateFrom}
      onChange={onFromChange}
    />
    <DatePicker
      mode="single"
      id="fr-date-to"
      label="To"
      value={dateTo}
      onChange={onToChange}
    />
    <button
      type="button"
      onClick={onRefresh}
      disabled={isLoading}
      className="flex items-center gap-2 rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
    >
      <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
      {isLoading ? 'Loading…' : 'Run report'}
    </button>
    {canExport && onExport && (
      <button
        type="button"
        onClick={onExport}
        className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        <Download size={14} />
        Export CSV
      </button>
    )}
  </div>
);

// ─── P&L Report ───────────────────────────────────────────────────────────────

export const ProfitAndLossReport: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const outletId = useAppSelector(selectEffectiveOutletId);
  const companyId = currentUser?.company?.id ?? null;

  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = `${today.slice(0, 7)}-01`;
  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [rows, setRows] = useState<PnlRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const data = await fetchProfitAndLoss(
        companyId,
        outletId,
        dateFrom,
        dateTo,
      );
      setRows(data);
      setLoaded(true);
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to load P&L');
    } finally {
      setIsLoading(false);
    }
  }, [companyId, outletId, dateFrom, dateTo]);

  const grouped = PNL_SECTION_ORDER.reduce<Record<string, PnlRow[]>>(
    (acc, sec) => {
      acc[sec] = rows.filter((r) => r.section === sec);
      return acc;
    },
    {} as Record<string, PnlRow[]>,
  );

  const subtotals = rows.filter((r) => r.isSubtotal);
  const grossProfit = subtotals.find((r) => r.section === 'Gross Profit');
  const netIncome = subtotals.find((r) => r.section === 'Net Income');

  const SECTION_COLORS: Partial<Record<PnlRow['section'], string>> = {
    Revenue: 'bg-green-50 text-green-800',
    COGS: 'bg-blue-50 text-blue-800',
    Expenses: 'bg-red-50 text-red-800',
  };

  const handleExport = () => {
    const nonSubtotal = rows.filter((r) => !r.isSubtotal);
    exportToCsv(
      'profit-and-loss',
      nonSubtotal.map((r) => ({
        Section: r.section,
        'Account Code': r.account_code ?? '',
        'Account Name': r.account_name,
        Amount: formatCurrencyValue(Math.abs(r.amount)),
      })),
    );
  };

  return (
    <div className="space-y-4">
      <DateRangeControls
        dateFrom={dateFrom}
        dateTo={dateTo}
        isLoading={isLoading}
        onFromChange={setDateFrom}
        onToChange={setDateTo}
        onRefresh={load}
        canExport={loaded && rows.length > 0}
        onExport={handleExport}
      />

      {loaded && (
        <>
          {/* Summary KPIs */}
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              {
                label: 'Total Revenue',
                value: grouped.Revenue?.reduce((s, r) => s + r.amount, 0) ?? 0,
                color: 'text-green-700',
              },
              {
                label: 'Gross Profit',
                value: grossProfit?.amount ?? 0,
                color:
                  (grossProfit?.amount ?? 0) >= 0
                    ? 'text-pos-primary'
                    : 'text-red-700',
              },
              {
                label: 'Net Income',
                value: netIncome?.amount ?? 0,
                color:
                  (netIncome?.amount ?? 0) >= 0
                    ? 'text-gray-900'
                    : 'text-red-700',
              },
            ].map(({ label, value, color }) => (
              <div
                key={label}
                className="rounded-2xl border border-gray-200 bg-white p-4"
              >
                <p className="text-xs text-gray-500">{label}</p>
                <p className={`mt-1 text-xl font-semibold ${color}`}>
                  {value < 0 ? '(' : ''}
                  {formatCurrencyValue(Math.abs(value))}
                  {value < 0 ? ')' : ''}
                </p>
              </div>
            ))}
          </div>

          {/* P&L Table */}
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            {(
              [
                'Revenue',
                'COGS',
                'Gross Profit',
                'Expenses',
                'Net Income',
              ] as PnlRow['section'][]
            ).map((section) => {
              const sectionRows = grouped[section] ?? [];
              const isSubtotalSection =
                section === 'Gross Profit' || section === 'Net Income';

              if (sectionRows.length === 0 && !isSubtotalSection) return null;

              const subtotal = isSubtotalSection
                ? (subtotals.find((r) => r.section === section)?.amount ?? 0)
                : sectionRows.reduce((s, r) => s + r.amount, 0);

              return (
                <div key={section}>
                  <div
                    className={`px-5 py-2 ${SECTION_COLORS[section as keyof typeof SECTION_COLORS] ?? 'bg-gray-100 text-gray-900'}`}
                  >
                    <span className="text-xs font-bold uppercase tracking-wider">
                      {section}
                    </span>
                  </div>

                  {!isSubtotalSection && sectionRows.length > 0 && (
                    <table className="w-full text-sm">
                      <tbody className="divide-y divide-gray-50">
                        {sectionRows.map((r) => (
                          <tr
                            key={r.account_code}
                            className="hover:bg-gray-50/50"
                          >
                            <td className="px-5 py-2 text-gray-600">
                              {r.account_code && (
                                <span className="mr-2 font-mono text-xs text-gray-400">
                                  {r.account_code}
                                </span>
                              )}
                              {r.account_name}
                            </td>
                            <td className="px-5 py-2 text-right font-medium text-gray-900">
                              {r.amount < 0 ? '(' : ''}
                              {formatCurrencyValue(Math.abs(r.amount))}
                              {r.amount < 0 ? ')' : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {/* Section subtotal */}
                  <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-5 py-2">
                    <span className="text-xs font-semibold text-gray-700">
                      {isSubtotalSection ? section : `Total ${section}`}
                    </span>
                    <span
                      className={`text-sm font-bold ${subtotal < 0 ? 'text-red-700' : 'text-gray-900'}`}
                    >
                      {subtotal < 0 ? '(' : ''}
                      {formatCurrencyValue(Math.abs(subtotal))}
                      {subtotal < 0 ? ')' : ''}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {!loaded && !isLoading && (
        <div className="flex h-40 items-center justify-center rounded-2xl border border-dashed border-gray-200 text-sm text-gray-400">
          Select a date range and click &ldquo;Run report&rdquo; to view the P&L
          statement.
        </div>
      )}
    </div>
  );
};

// ─── Tax / VAT Report ─────────────────────────────────────────────────────────

export const TaxVatReport: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const outletId = useAppSelector(selectEffectiveOutletId);
  const companyId = currentUser?.company?.id ?? null;

  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = `${today.slice(0, 7)}-01`;
  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [rows, setRows] = useState<TaxReportRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const data = await fetchTaxReport(companyId, outletId, dateFrom, dateTo);
      setRows(data);
      setLoaded(true);
    } catch (err) {
      apiError(
        err instanceof Error ? err.message : 'Failed to load tax report',
      );
    } finally {
      setIsLoading(false);
    }
  }, [companyId, outletId, dateFrom, dateTo]);

  const totalCollected = rows.reduce((s, r) => s + r.credit, 0);
  const totalRecoverable = rows.reduce((s, r) => s + r.debit, 0);
  const netVatPayable = totalCollected - totalRecoverable;

  const handleExport = () => {
    exportToCsv(
      'tax-vat-report',
      rows.map((r) => ({
        Date: r.entry_date,
        Source: r.source_module,
        Type: r.reference_type ?? '',
        'Output (Credit)': r.credit > 0 ? formatCurrencyValue(r.credit) : '—',
        'Input (Debit)': r.debit > 0 ? formatCurrencyValue(r.debit) : '—',
        'Running Balance': formatCurrencyValue(r.running_balance),
      })),
    );
  };

  return (
    <div className="space-y-4">
      <DateRangeControls
        dateFrom={dateFrom}
        dateTo={dateTo}
        isLoading={isLoading}
        onFromChange={setDateFrom}
        onToChange={setDateTo}
        onRefresh={load}
        canExport={loaded && rows.length > 0}
        onExport={handleExport}
      />

      {loaded && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              {
                label: 'Output VAT (collected)',
                value: totalCollected,
                color: 'text-red-700',
              },
              {
                label: 'Input VAT (recoverable)',
                value: totalRecoverable,
                color: 'text-green-700',
              },
              {
                label: 'Net VAT Payable',
                value: netVatPayable,
                color: netVatPayable >= 0 ? 'text-gray-900' : 'text-green-700',
              },
            ].map(({ label, value, color }) => (
              <div
                key={label}
                className="rounded-2xl border border-gray-200 bg-white p-4"
              >
                <p className="text-xs text-gray-500">{label}</p>
                <p className={`mt-1 text-xl font-semibold ${color}`}>
                  {formatCurrencyValue(value)}
                </p>
              </div>
            ))}
          </div>

          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-5 py-3">
              <h4 className="text-sm font-semibold text-gray-900">
                VAT Ledger (Account 2040)
              </h4>
            </div>
            {rows.length === 0 ? (
              <div className="flex h-20 items-center justify-center text-sm text-gray-400">
                No VAT entries in this period
              </div>
            ) : (
              <div className="max-h-[32rem] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
                    <tr>
                      {[
                        'Date',
                        'Source',
                        'Type',
                        'Output (Credit)',
                        'Input (Debit)',
                        'Running Balance',
                      ].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rows.map((r) => (
                      <tr
                        key={`${r.entry_date}-${r.source_module}-${r.reference_type ?? 'none'}-${r.debit}-${r.credit}-${r.running_balance}`}
                        className="hover:bg-gray-50/50"
                      >
                        <td className="px-4 py-2 text-gray-700">
                          {fmtDate(r.entry_date)}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${srcBadge(r.source_module)}`}
                          >
                            {r.source_module}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-500">
                          {r.reference_type ?? '—'}
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-red-700">
                          {r.credit > 0 ? formatCurrencyValue(r.credit) : '—'}
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-green-700">
                          {r.debit > 0 ? formatCurrencyValue(r.debit) : '—'}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-900">
                          {formatCurrencyValue(r.running_balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {!loaded && !isLoading && (
        <div className="flex h-40 items-center justify-center rounded-2xl border border-dashed border-gray-200 text-sm text-gray-400">
          Select a date range and click &ldquo;Run report&rdquo; to view the tax
          report.
        </div>
      )}
    </div>
  );
};

// ─── COGS Report ──────────────────────────────────────────────────────────────

export const CogsReport: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const outletId = useAppSelector(selectEffectiveOutletId);
  const companyId = currentUser?.company?.id ?? null;

  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = `${today.slice(0, 7)}-01`;
  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [rows, setRows] = useState<CogsReportRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const data = await fetchCogsReport(companyId, outletId, dateFrom, dateTo);
      setRows(data);
      setLoaded(true);
    } catch (err) {
      apiError(
        err instanceof Error ? err.message : 'Failed to load COGS report',
      );
    } finally {
      setIsLoading(false);
    }
  }, [companyId, outletId, dateFrom, dateTo]);

  const totalCogs = rows.reduce((s, r) => s + r.debit - r.credit, 0);

  const byAccount = Array.from(
    rows.reduce((map, r) => {
      const key = r.account_code;
      const cur = map.get(key) ?? {
        code: r.account_code,
        name: r.account_name,
        total: 0,
      };
      cur.total += r.debit - r.credit;
      map.set(key, cur);
      return map;
    }, new Map<string, { code: string; name: string; total: number }>()),
  ).map(([, v]) => v);

  const handleExport = () => {
    exportToCsv(
      'cogs-report',
      rows.map((r) => ({
        Date: r.entry_date,
        Account: r.account_code,
        'Account Name': r.account_name,
        Source: r.source_module,
        Debit: r.debit > 0 ? formatCurrencyValue(r.debit) : '—',
        Credit: r.credit > 0 ? formatCurrencyValue(r.credit) : '—',
      })),
    );
  };

  return (
    <div className="space-y-4">
      <DateRangeControls
        dateFrom={dateFrom}
        dateTo={dateTo}
        isLoading={isLoading}
        onFromChange={setDateFrom}
        onToChange={setDateTo}
        onRefresh={load}
        canExport={loaded && rows.length > 0}
        onExport={handleExport}
      />

      {loaded && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-gray-200 bg-white p-4">
              <p className="text-xs text-gray-500">Total COGS</p>
              <p className="mt-1 text-xl font-semibold text-gray-900">
                {formatCurrencyValue(totalCogs)}
              </p>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-4">
              <p className="text-xs text-gray-500">COGS Accounts</p>
              <p className="mt-1 text-xl font-semibold text-gray-900">
                {byAccount.length}
              </p>
            </div>
          </div>

          {/* By account summary */}
          {byAccount.length > 0 && (
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
              <div className="border-b border-gray-100 px-5 py-3">
                <h4 className="text-sm font-semibold text-gray-900">
                  COGS by Account
                </h4>
              </div>
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    {['Account', 'Name', 'Total COGS'].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {byAccount.map((a) => (
                    <tr key={a.code} className="hover:bg-gray-50/50">
                      <td className="px-4 py-2 font-mono text-xs text-gray-500">
                        {a.code}
                      </td>
                      <td className="px-4 py-2 text-gray-900">{a.name}</td>
                      <td className="px-4 py-2 text-right font-semibold text-gray-900">
                        {formatCurrencyValue(a.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                  <tr>
                    <td
                      colSpan={2}
                      className="px-4 py-2 text-xs font-semibold text-gray-600"
                    >
                      Total
                    </td>
                    <td className="px-4 py-2 text-right text-sm font-bold text-gray-900">
                      {formatCurrencyValue(totalCogs)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Detailed entries */}
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-5 py-3">
              <h4 className="text-sm font-semibold text-gray-900">
                COGS Journal Lines ({rows.length})
              </h4>
            </div>
            {rows.length === 0 ? (
              <div className="flex h-20 items-center justify-center text-sm text-gray-400">
                No COGS entries in this period
              </div>
            ) : (
              <div className="max-h-[32rem] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
                    <tr>
                      {['Date', 'Account', 'Source', 'Debit', 'Credit'].map(
                        (h) => (
                          <th
                            key={h}
                            className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500"
                          >
                            {h}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rows.map((r) => (
                      <tr
                        key={`${r.entry_date}-${r.account_code}-${r.source_module}-${r.debit}-${r.credit}`}
                        className="hover:bg-gray-50/50"
                      >
                        <td className="px-4 py-2 text-gray-700">
                          {fmtDate(r.entry_date)}
                        </td>
                        <td className="px-4 py-2">
                          <span className="font-mono text-xs text-gray-400">
                            {r.account_code}
                          </span>{' '}
                          {r.account_name}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${srcBadge(r.source_module)}`}
                          >
                            {r.source_module}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-gray-900">
                          {r.debit > 0 ? formatCurrencyValue(r.debit) : '—'}
                        </td>
                        <td className="px-4 py-2 text-right text-green-700">
                          {r.credit > 0 ? formatCurrencyValue(r.credit) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {!loaded && !isLoading && (
        <div className="flex h-40 items-center justify-center rounded-2xl border border-dashed border-gray-200 text-sm text-gray-400">
          Select a date range and click &ldquo;Run report&rdquo; to view COGS.
        </div>
      )}
    </div>
  );
};

// ─── Deferred Revenue Report ──────────────────────────────────────────────────

export const DeferredRevenueReport: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const outletId = useAppSelector(selectEffectiveOutletId);
  const companyId = currentUser?.company?.id ?? null;

  const [rows, setRows] = useState<DeferredRevenueRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadDeferredRevenue = async () => {
      if (!companyId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const data = await fetchDeferredRevenue(companyId, outletId);
        if (isMounted) setRows(data);
      } catch (err) {
        apiError(
          err instanceof Error
            ? err.message
            : 'Failed to load deferred revenue',
        );
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadDeferredRevenue();

    return () => {
      isMounted = false;
    };
  }, [companyId, outletId]);

  const ACCOUNT_LABELS: Record<string, string> = {
    '2100': 'Unearned Room Revenue',
    '2120': 'Advance Guest Deposits',
    '1110': 'Guest Receivables',
  };

  const totalDeferred = rows
    .filter((r) => ['2100', '2120'].includes(r.account_code))
    .reduce((s, r) => s + Math.abs(r.closing_balance), 0);

  const totalReceivable = rows
    .filter((r) => r.account_code === '1110')
    .reduce((s, r) => s + r.closing_balance, 0);

  const handleExport = () => {
    exportToCsv(
      'deferred-revenue',
      rows.map((r) => ({
        'Account Code': r.account_code,
        'Account Name': ACCOUNT_LABELS[r.account_code] ?? r.account_name,
        'Total Debits': formatCurrencyValue(r.total_debits),
        'Total Credits': formatCurrencyValue(r.total_credits),
        Balance: formatCurrencyValue(Math.abs(r.closing_balance)),
      })),
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white p-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            Deferred &amp; Earned Revenue
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Current balances for unearned room revenue, advance deposits, and
            guest receivables. Refreshes automatically on load.
          </p>
        </div>
        {rows.length > 0 && (
          <button
            type="button"
            onClick={handleExport}
            className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Download size={14} />
            Export CSV
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex h-28 items-center justify-center rounded-2xl border border-gray-200 bg-white text-sm text-gray-400">
          Loading…
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-gray-200 bg-white p-4">
              <p className="text-xs text-gray-500">
                Total Unearned (liability)
              </p>
              <p className="mt-1 text-xl font-semibold text-orange-700">
                {formatCurrencyValue(totalDeferred)}
              </p>
              <p className="mt-0.5 text-xs text-gray-400">
                Revenue not yet earned
              </p>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-4">
              <p className="text-xs text-gray-500">Guest Receivables (asset)</p>
              <p className="mt-1 text-xl font-semibold text-pos-primary">
                {formatCurrencyValue(totalReceivable)}
              </p>
              <p className="mt-0.5 text-xs text-gray-400">
                Revenue earned, not collected
              </p>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-5 py-3">
              <h4 className="text-sm font-semibold text-gray-900">
                Account Balances
              </h4>
            </div>
            {rows.length === 0 ? (
              <div className="flex h-20 items-center justify-center text-sm text-gray-400">
                No activity on these accounts yet
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">
                      Code
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">
                      Account
                    </th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">
                      Total Debits
                    </th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">
                      Total Credits
                    </th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">
                      Balance
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {rows.map((r) => (
                    <tr key={r.account_code} className="hover:bg-gray-50/50">
                      <td className="px-4 py-2.5 font-mono text-xs text-gray-400">
                        {r.account_code}
                      </td>
                      <td className="px-4 py-2.5 font-medium text-gray-900">
                        {ACCOUNT_LABELS[r.account_code] ?? r.account_name}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-700">
                        {formatCurrencyValue(r.total_debits)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-700">
                        {formatCurrencyValue(r.total_credits)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold text-gray-900">
                        {formatCurrencyValue(Math.abs(r.closing_balance))}
                        <span className="ml-1 text-xs font-normal text-gray-400">
                          {r.closing_balance >= 0 ? 'DR' : 'CR'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};
